import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);

  const navLinks = [
    { path: "/", label: "Home" },
    {
      path: "/portfolio",
      label: "Matte Painting & Concept art",
      lines: ["Matte Painting &", "Concept art"],
    },
    { path: "/visual-development", label: "Visual Development" },
    { path: "/book-cover-design", label: "Book Covers" },
    {
      path: "/services",
      label: "Services & VFX Software Development",
      lines: ["Services &", "VFX Software", "Development"],
    },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      document.documentElement.style.setProperty("--header-height", `${el.offsetHeight}px`);
    };

    update();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 bg-[#25282c] border-b border-black/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col items-center pt-10 pb-7">
          {/* Logo (centered + spaced-out like legacy site) */}
          <Link to="/" className="inline-flex items-center">
            <div className="text-3xl md:text-4xl font-medium tracking-[0.55em] uppercase text-[#ffa94c] transition-colors hover:text-white">
              SKYLINE VFX
            </div>
          </Link>

          {/* Desktop Nav (centered below logo). Shown from 1280px; tighter gaps until 1600px so all links fit. */}
          <nav className="hidden xl:flex items-center gap-8 min-[1600px]:gap-20 mt-9">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-semibold uppercase tracking-[0.24em] text-center leading-tight transition-colors ${
                  link.path === "/visual-development" || link.path === "/services"
                    ? "w-[10.5rem]"
                    : link.path === "/portfolio"
                      ? "w-[12rem]"
                      : "w-[7.5rem]"
                } ${
                  isActive(link.path)
                    ? "text-[#ffa94c]"
                    : "text-white hover:text-[#ffa94c]"
                }`}
              >
                {Array.isArray(link.lines)
                  ? link.lines.map((line, i) => (
                      <span key={i} className="block">
                        {line}
                      </span>
                    ))
                  : link.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-2 text-white hover:text-[#ffa94c] mt-4"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="xl:hidden mt-4 w-full border-t border-white/10 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 text-sm font-semibold uppercase tracking-[0.24em] ${
                    isActive(link.path) ? "text-[#ffa94c]" : "text-white hover:text-[#ffa94c]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
